/*──────────────────────────────────────────────────────────────
 *  File: src/components/ExplorerModal.jsx   — fully regenerated 2025-10-26
 *
 *  Lease File Explorer modal (FieldTicket)
 *
 *  Behavior
 *  • Tenant from hostname subdomain (stasney.ogfieldticket.ogpumper.net → "stasney")
 *  • Default view = LIST (remembered)
 *  • Previews open at true 100% (1×)
 *  • Modal ≤ 90vh with overflow-y-auto
 *  • Uses FileManager which forwards ?token and always sets company=subdomain
 *──────────────────────────────────────────────────────────────*/

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { listFiles, signedUrl, companySlug } from "../utils/FileManager";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faFileExcel,
  faFilePdf,
  faFileWord,
  faFolder,
  faFolderOpen,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faSearch,
  faSortAmountDown,
  faTimes,
  faUndo,
  faBars,
  faTh,
} from "@fortawesome/free-solid-svg-icons";

/*──────────────── constants ─────────────────────────────────*/
const LS = { VIEW_MODE: "hpFeViewMode" };

/* file-type helpers */
const isImage = (k) => /\.(jpe?g|png|gif|webp|bmp|avif|tiff?)$/i.test(k);
const isPdf = (k) => /\.pdf$/i.test(k);
const isExcel = (k) => /\.(xlsx?|xlsm?)$/i.test(k);
const isWord = (k) => /\.(docx?|rtf)$/i.test(k);

const officeViewerUrl = (raw) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
    raw
  )}`;

const fmtSize = (b) =>
  b >= 1 << 20 ? `${(b / (1 << 20)).toFixed(1)} MB` : `${(b / 1024) | 0} KB`;

/*──────────────── generic modal shell ───────────────────────*/
const Modal = ({ open, onClose, children, theme }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        className={`relative w-full max-w-5xl mx-4 md:mx-0 rounded-lg shadow-2xl overflow-y-auto ${
          theme === "dark" ? "bg-gray-800" : "bg-white"
        }`}
        style={{ maxHeight: "90vh" }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-300 hover:text-white text-2xl focus:outline-none z-50"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        {children}
      </div>
    </div>
  );
};

/*──────────────── ExplorerModal ─────────────────────────────*/
const ExplorerModal = ({
  open,
  onClose,
  theme = "light",
  // Optional cosmetic label if caller wants to override
  companyName,
}) => {
  /*──────── state ──────────────*/
  const [prefix, setPrefix] = useState(""); // "Leases/" + prefix
  const [fetching, setFetching] = useState(false);
  const [feSearch, setFeSearch] = useState("");
  const [feSortBy, setFeSortBy] = useState("name");
  const [feSortAsc, setFeSortAsc] = useState(true);
  const [feView, setFeView] = useState(
    (typeof localStorage !== "undefined" &&
      localStorage.getItem(LS.VIEW_MODE)) ||
      "list"
  );
  const [activeIdx, setActiveIdx] = useState(null);
  const [zoom, setZoom] = useState(1);

  const gridRef = useRef(null);
  const listCache = useRef(new Map());

  /*──────── constants ───────────────────────────────────────*/
  const rootPrefix = "Leases/"; // API prefix base
  const tenant = companySlug(); // ← hostname subdomain
  const label = companyName || tenant;

  /*──────── list loader ─────────────────────────────────────*/
  const loadPrefix = useCallback(
    async (p) => {
      if (listCache.current.has(p)) return;
      setFetching(true);
      try {
        const apiPrefix = rootPrefix + p; // "Leases/" or "Leases/Foo/"
        const objs = await listFiles(apiPrefix, tenant);

        const dirs = [];
        const files = [];

        for (const o of objs) {
          const key = o?.Key || "";
          if (!key) continue;

          const name = key.split("/").pop() || "file";

          if (o.type === "folder") {
            const folderName = name;
            if (folderName) dirs.push(folderName);
            continue;
          }

          const kind = isPdf(key)
            ? "pdf"
            : isExcel(key)
            ? "xls"
            : isWord(key)
            ? "doc"
            : isImage(key)
            ? "img"
            : null;

          files.push({
            key,
            name,
            date: o.LastModified ? new Date(o.LastModified) : null,
            size: o.Size || 0,
            kind,
          });
        }

        // presign in parallel
        const urls = await Promise.all(files.map((f) => signedUrl(f.key)));
        files.forEach((f, i) => (f.url = urls[i]));

        dirs.sort((a, b) => a.localeCompare(b));

        listCache.current.set(p, { dirs, files });
      } catch (err) {
        console.error("Explorer load:", err);
      } finally {
        setFetching(false);
      }
    },
    [rootPrefix, tenant]
  );

  /*──────── initial load / prefix change ────────────────────*/
  useEffect(() => {
    if (open) loadPrefix(prefix);
  }, [open, prefix, loadPrefix]);

  /*──────── filtered & sorted lists ─────────────────────────*/
  const data = listCache.current.get(prefix) || { dirs: [], files: [] };
  const [dirHits, fileHits] = useMemo(() => {
    const q = feSearch.trim().toLowerCase();

    const dirs = !q
      ? data.dirs
      : data.dirs.filter((d) => d.toLowerCase().includes(q));

    let files = !q
      ? data.files
      : data.files.filter(({ name }) => name.toLowerCase().includes(q));

    files = [...files].sort((a, b) => {
      const dir = feSortAsc ? 1 : -1;
      if (feSortBy === "name") return a.name.localeCompare(b.name) * dir;
      if (feSortBy === "date")
        return ((a.date?.getTime() || 0) - (b.date?.getTime() || 0)) * dir;
      if (feSortBy === "size") return (a.size - b.size) * dir;
      return 0;
    });
    return [dirs, files];
  }, [data, feSearch, feSortBy, feSortAsc]);

  /*──────── preview helpers ─────────────────────────────────*/
  const openPrev = (i) => {
    setZoom(1);
    setActiveIdx(i);
  };
  const closePrev = () => setActiveIdx(null);
  const navPrev = (d) =>
    setActiveIdx((i) =>
      i === null ? null : (i + d + fileHits.length) % fileHits.length
    );

  /*──────── keyboard shortcuts ──────────────────────────────*/
  useEffect(() => {
    const h = (e) => {
      if (activeIdx === null) return;
      if (e.key === "Escape") closePrev();
      if (e.key === "ArrowRight") navPrev(1);
      if (e.key === "ArrowLeft") navPrev(-1);
      if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(z + 0.25, 5));
      if (e.key === "-" || e.key === "_")
        setZoom((z) => Math.max(z - 0.25, 0.5));
      if (e.key === "0") setZoom(1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [activeIdx, fileHits.length]);

  useEffect(() => {
    gridRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [prefix]);

  /*───────────────────────────── JSX ─────────────────────────*/
  return (
    <Modal open={open} onClose={onClose} theme={theme}>
      <div
        className={`p-6 ${
          theme === "dark"
            ? "bg-gray-900 text-gray-100"
            : "bg-white text-gray-800"
        }`}
      >
        <h3 className="text-2xl font-semibold mb-6 flex items-center gap-2">
          <FontAwesomeIcon icon={faFolderOpen} /> Leases – {label}
        </h3>

        {/* breadcrumb */}
        <nav className="text-sm mb-4 flex flex-wrap gap-1 text-blue-700 dark:text-blue-400">
          <span
            className="cursor-pointer hover:underline"
            onClick={() => setPrefix("")}
          >
            {label}
          </span>
          {prefix
            .replace(/\/$/, "")
            .split("/")
            .filter(Boolean)
            .map((seg, i, arr) => {
              const upto = arr.slice(0, i + 1).join("/") + "/";
              return (
                <React.Fragment key={upto}>
                  <span>/</span>
                  <span
                    className="cursor-pointer hover:underline"
                    onClick={() => setPrefix(upto)}
                  >
                    {seg}
                  </span>
                </React.Fragment>
              );
            })}
        </nav>

        {/* search / sort / view toggle */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          {/* search */}
          <label className="relative flex-1">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search…"
              className={`w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 ${
                theme === "dark"
                  ? "bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-400 focus:ring-blue-500"
                  : "bg-white border-gray-300 text-gray-800 placeholder-gray-500 focus:ring-blue-600"
              }`}
              value={feSearch}
              onChange={(e) => setFeSearch(e.target.value)}
            />
          </label>

          {/* sort + toggle */}
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faSortAmountDown} />
            <select
              value={feSortBy}
              onChange={(e) => setFeSortBy(e.target.value)}
              className={`rounded-lg py-2 px-3 border ${
                theme === "dark"
                  ? "bg-gray-800 border-gray-700 text-gray-200"
                  : "bg-white border-gray-300 text-gray-700"
              }`}
            >
              <option value="name">Name</option>
              <option value="date">Date</option>
              <option value="size">Size</option>
            </select>
            <button
              className={`px-3 py-2 rounded-lg border ${
                theme === "dark"
                  ? "bg-gray-800 border-gray-700 hover:bg-gray-700"
                  : "bg-white border-gray-300 hover:bg-gray-200"
              }`}
              onClick={() => setFeSortAsc((b) => !b)}
              title="Toggle sort"
            >
              {feSortAsc ? "↑" : "↓"}
            </button>
            <button
              className={`ml-4 px-3 py-2 rounded-lg border ${
                feView === "list"
                  ? "bg-blue-600 text-white"
                  : theme === "dark"
                  ? "bg-gray-800 border-gray-700 hover:bg-gray-700"
                  : "bg-white border-gray-300 hover:bg-gray-200"
              }`}
              onClick={() => {
                const v = feView === "list" ? "grid" : "list";
                setFeView(v);
                try {
                  localStorage.setItem(LS.VIEW_MODE, v);
                } catch {}
              }}
              title="Toggle view"
            >
              <FontAwesomeIcon icon={feView === "list" ? faTh : faBars} />
            </button>
          </div>
        </div>

        {/* back */}
        {prefix && (
          <button
            className="mb-4 flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline"
            onClick={() => setPrefix(prefix.replace(/[^/]+\/$/, ""))}
          >
            <FontAwesomeIcon icon={faChevronLeft} /> Back
          </button>
        )}

        {/* loading */}
        {fetching && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500" />
          </div>
        )}

        {/* empty */}
        {!fetching && dirHits.length === 0 && fileHits.length === 0 && (
          <p className="text-center text-gray-500 dark:text-gray-400">
            No folders or files.
          </p>
        )}

        {/* folders */}
        {dirHits.length > 0 && (
          <>
            <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">
              Folders
            </h4>
            <div
              className={
                feView === "grid"
                  ? "grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
                  : "flex flex-col divide-y divide-gray-200 dark:divide-gray-700"
              }
            >
              {dirHits.map((d) =>
                feView === "grid" ? (
                  <div
                    key={d}
                    className={`group cursor-pointer flex flex-col items-center justify-center p-4 rounded-lg shadow border ${
                      theme === "dark"
                        ? "bg-gray-800 border-gray-700 hover:bg-gray-700"
                        : "bg-white border-gray-200 hover:bg-blue-50"
                    }`}
                    onClick={() => setPrefix(prefix + d + "/")}
                  >
                    <FontAwesomeIcon
                      icon={faFolder}
                      className="h-8 w-8 text-yellow-500 mb-2 group-hover:text-yellow-600"
                    />
                    <span className="truncate max-w-full text-xs">{d}</span>
                  </div>
                ) : (
                  <div
                    key={d}
                    className={`flex items-center gap-3 py-2 px-2 cursor-pointer ${
                      theme === "dark"
                        ? "hover:bg-gray-700"
                        : "hover:bg-blue-50"
                    }`}
                    onClick={() => setPrefix(prefix + d + "/")}
                  >
                    <FontAwesomeIcon
                      icon={faFolder}
                      className="text-yellow-500"
                    />
                    <span className="flex-1 truncate">{d}</span>
                  </div>
                )
              )}
            </div>
          </>
        )}

        {/* files */}
        {fileHits.length > 0 && (
          <>
            <h4
              id="grid-start"
              ref={gridRef}
              className="text-lg font-semibold mt-8 mb-3 text-gray-800 dark:text-gray-200"
            >
              Files ({fileHits.length})
            </h4>

            {/* GRID view */}
            {feView === "grid" ? (
              <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {fileHits.map((f, idx) => {
                  if (f.kind === "img")
                    return (
                      <img
                        key={f.key}
                        src={f.url}
                        alt={f.name}
                        loading="lazy"
                        className="w-full h-32 object-cover rounded-lg shadow cursor-pointer hover:opacity-80"
                        onClick={() => openPrev(idx)}
                      />
                    );
                  const icon =
                    f.kind === "pdf"
                      ? faFilePdf
                      : f.kind === "xls"
                      ? faFileExcel
                      : faFileWord;
                  const base =
                    f.kind === "pdf"
                      ? ["bg-red-50 text-red-700", "hover:bg-red-100"]
                      : f.kind === "xls"
                      ? ["bg-green-50 text-green-700", "hover:bg-green-100"]
                      : ["bg-blue-50 text-blue-700", "hover:bg-blue-100"];
                  return (
                    <div
                      key={f.key}
                      className={`w-full h-32 flex flex-col justify-center items-center rounded-lg shadow cursor-pointer ${base[0]} ${base[1]}`}
                      onClick={() => openPrev(idx)}
                    >
                      <FontAwesomeIcon icon={icon} className="h-8 w-8 mb-2" />
                      <span className="text-xs px-2 text-center truncate">
                        {f.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* LIST view */
              <table className="w-full text-sm border rounded-lg overflow-hidden border-gray-200 dark:border-gray-700">
                <thead
                  className={
                    theme === "dark"
                      ? "bg-gray-800 text-gray-200"
                      : "bg-gray-200 text-gray-700"
                  }
                >
                  <tr>
                    <th className="text-left px-3 py-2 w-8"></th>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Size</th>
                  </tr>
                </thead>
                <tbody>
                  {fileHits.map((f, idx) => {
                    const icon =
                      f.kind === "pdf"
                        ? faFilePdf
                        : f.kind === "xls"
                        ? faFileExcel
                        : faFileWord;
                    return (
                      <tr
                        key={f.key}
                        className={`border-b cursor-pointer ${
                          theme === "dark"
                            ? "border-gray-700 hover:bg-gray-800"
                            : "border-gray-200 hover:bg-gray-100"
                        }`}
                        onClick={() => openPrev(idx)}
                      >
                        <td className="px-3 py-2">
                          <FontAwesomeIcon icon={icon} />
                        </td>
                        <td className="px-3 py-2 truncate">{f.name}</td>
                        <td className="px-3 py-2">
                          {f.date ? f.date.toLocaleDateString() : "—"}
                        </td>
                        <td className="px-3 py-2">{fmtSize(f.size)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* preview overlay */}
        {activeIdx !== null && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && closePrev()}
          >
            {/* nav controls */}
            <button
              className="absolute left-4 md:left-8 text-white/90 hover:text-white text-4xl z-[110]"
              onClick={() => navPrev(-1)}
              aria-label="Previous"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              className="absolute right-4 md:right-8 text-white/90 hover:text-white text-4xl z-[110]"
              onClick={() => navPrev(1)}
              aria-label="Next"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>

            {/* preview wrapper */}
            <div className="relative max-w-[95vw] max-h-[80vh] p-2 md:p-4">
              {/* close icon */}
              <button
                className="absolute -top-4 -right-4 text-white bg-black/70 hover:bg-black/90 rounded-full p-2 focus:outline-none z-[110]"
                onClick={closePrev}
                aria-label="Close Preview"
              >
                <FontAwesomeIcon icon={faTimes} size="sm" />
              </button>

              {/* zoom controls */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 flex gap-6 text-white z-[110]">
                <button
                  onClick={() => setZoom((z) => Math.min(z + 0.25, 5))}
                  className="bg-black/70 hover:bg-black/90 rounded-full p-4"
                  aria-label="Zoom in"
                >
                  <FontAwesomeIcon icon={faMagnifyingGlassPlus} size="lg" />
                </button>
                <button
                  onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))}
                  className="bg-black/70 hover:bg-black/90 rounded-full p-4"
                  aria-label="Zoom out"
                >
                  <FontAwesomeIcon icon={faMagnifyingGlassMinus} size="lg" />
                </button>
                <button
                  onClick={() => setZoom(1)}
                  className="bg-black/70 hover:bg-black/90 rounded-full p-4"
                  aria-label="Reset zoom"
                >
                  <FontAwesomeIcon icon={faUndo} size="lg" />
                </button>
              </div>

              {/* preview content */}
              <div
                className="overflow-auto"
                style={{ maxWidth: "95vw", maxHeight: "85vh" }}
              >
                {(() => {
                  const f = fileHits[activeIdx];
                  const baseStyle = {
                    maxWidth: "95vw",
                    maxHeight: "85vh",
                    width: "95vw",
                    height: "85vh",
                    borderRadius: "0.5rem",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
                  };

                  if (f.kind === "img") {
                    return (
                      <img
                        src={f.url}
                        alt={f.name}
                        style={{
                          ...baseStyle,
                          transform: `scale(${zoom})`,
                          transformOrigin: "top left",
                          objectFit: "contain",
                        }}
                      />
                    );
                  }

                  if (f.kind === "pdf") {
                    const pdfZoom = Math.round(zoom * 100);
                    return (
                      <object
                        key={pdfZoom}
                        data={`${f.url}#view=FitH&zoom=${pdfZoom}`}
                        type="application/pdf"
                        style={baseStyle}
                      >
                        <p className="text-white">
                          Unable to preview PDF.&nbsp;
                          <a
                            href={f.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            Download&nbsp;{f.name}
                          </a>
                        </p>
                      </object>
                    );
                  }

                  if (f.kind === "doc" || f.kind === "xls") {
                    return (
                      <iframe
                        key={Math.round(zoom * 100)}
                        src={officeViewerUrl(f.url)}
                        title={f.name}
                        style={{
                          ...baseStyle,
                          transform: `scale(${zoom})`,
                          transformOrigin: "top left",
                          border: "0",
                        }}
                      />
                    );
                  }

                  return (
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white underline text-lg"
                    >
                      Open&nbsp;{f.name}
                    </a>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default React.memo(ExplorerModal);
